export enum NotificationType {
  INFO = 1,
  WARNING = 2,
  ALERT = 3,
  SUCCESS = 4,
}

export enum NotificationPriority {
  LOW = 1,
  MEDIUM = 2,
  HIGH = 3,
  URGENT = 4,
}

export enum NotificationChannel {
  EMAIL = 1,
  IN_APP = 2,
  SMS = 3,
  WHATSAPP = 4,
  LINE = 5,
}

export enum NotificationStatus {
  PENDING = 1,
  SENT = 2,
  DELIVERED = 3,
  READ = 4,
  FAILED = 5,
  EXPIRED = 6,
  ARCHIVED = 7,
}

export enum NotificationSortBy {
  CreatedAt = 1,
  Priority = 2,
  Status = 3,
  Type = 4,
  ReadAt = 5,
  PostponedUntil = 6,
}

export enum SortOrder {
  Asc = 'Asc',
  Desc = 'Desc',
}

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: NotificationType;
  priority: NotificationPriority;
  channel: NotificationChannel;
  category?: number | null;
  createdAt: Date | string;
  isRead: boolean;
  isPinned: boolean;
  isPostponed: boolean;
  readAt?: Date | string;
  postponedUntil?: Date | string | null;
  dispatch?: NotificationDispatch;
  translationsJson?: string | null;
  locale?: string | null;
  renderedTitle?: string;
  renderedMessage?: string;
}

export interface NotificationDetail extends Notification {
  relatedEntityType?: string;
  relatedEntityId?: string;
  actions?: { [key: string]: string };
  expiresAt?: Date | string;
  metadata?: { [key: string]: unknown };
  dispatch?: NotificationDispatch;
}

export interface NotificationDispatch {
  id: string;
  notificationId: string;
  renderedTitle?: string;
  renderedMessage?: string;
  translationsJson?: string | null;
  locale?: string;
  status: NotificationStatus;
  queuedAt: Date | string;
  sentAt?: Date | string;
  deliveredAt?: Date | string;
  readAt?: Date | string;
  expiresAt?: Date | string;
  lastAttemptAt?: Date | string;
  attemptCount: number;
  lastErrorCode?: string;
  errorMessage?: string;
  isPinned: boolean;
  isPostponed: boolean;
  postponedUntil?: Date | string;
}

export interface NotificationCount {
  unreadCount: number;
}

export interface NotificationPagination {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface NotificationAppliedFilters {
  userId?: number;
  category?: number | null;
  type?: NotificationType;
  priority?: NotificationPriority;
  status?: NotificationStatus;
  dateFrom?: Date | string;
  dateTo?: Date | string;
  read?: boolean;
  tags?: string[];
  sortBy?: NotificationSortBy;
  sortOrder?: SortOrder;
}

export interface NotificationListResponse {
  items: Notification[];
  pagination: NotificationPagination;
  filters?: NotificationAppliedFilters;
  summary?: NotificationsSummary;
}

export type PostponedFilterValue = 'true' | 'false' | 'none' | null;

export interface NotificationFilters {
  userId?: number | null;
  category?: number | null;
  type?: NotificationType | null;
  priority?: NotificationPriority | null;
  status?: NotificationStatus | null;
  dateFrom?: Date | null;
  dateTo?: Date | null;
  read?: boolean | null;
  postponed?: PostponedFilterValue;
  tags?: string[] | null;
  searchText?: string | null;
  sortBy?: NotificationSortBy;
  sortOrder?: SortOrder;
}

export interface MarkAllAsReadResult {
  markedCount: number;
}

export enum BatchNotificationAction {
  MARK_READ = 0,
  ARCHIVE = 1,
  MARK_UNREAD = 2,
}

export interface BatchNotificationActionRequest {
  notificationIds: string[];
  action: 'MARK_READ' | 'MARK_UNREAD' | 'ARCHIVE';
}

export interface BatchOperationResult {
  successCount: number;
  failedCount: number;
  failedIds: string[];
}

export interface NotificationReceivedEvent {
  notification: Notification;
}

export interface NotificationReadEvent {
  notificationId: string;
  readAt: string;
}

export interface AllNotificationsReadEvent {
  markedCount: number;
}

export interface NotificationArchivedEvent {
  notificationId: string;
}

export interface CounterUpdatedEvent {
  unreadCount: number;
}

export interface BatchOperationEvent {
  operation: string;
  notificationIds: string[];
  successCount: number;
}

export interface NotificationUpdatedEvent {
  notificationId: string;
}

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'failed';

export interface NotificationSettings {
  soundEnabled: boolean;
  soundVolume: number;
  toastEnabled: boolean;
  toastDuration: number;
}

export interface PostponeNotificationResult {
  dispatchId: string;
  isPostponed: boolean;
  postponedUntil?: Date | string;
  postponedByUserId?: number;
  postponedDays?: number;
}

export interface CancelPostponeResponse {
  message: string;
  notificationId: string;
  cancelledAt: Date | string;
}

export interface NotificationsSummary {
  totalUnread: number;
  totalPostponed: number;
  totalPinned: number;
}

export interface Tag {
  id: number;
  name: string;
  description?: string;
  color?: string;
  isSystemTag: boolean;
  createdAt: Date | string;
  updatedAt?: Date | string;
}

export interface TagListResponse {
  tags: Tag[];
  total: number;
}

export interface PinNotificationResult {
  dispatchId: string;
  isPinned: boolean;
  pinnedAt?: Date | string;
  pinnedByUserId?: number;
  totalPinnedCount: number;
}
